import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../../components/Table";
import { formatMoney } from "../../../utils/formatMoney";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import type { ImportValidProduct } from "../../../api/productsApi";
import type { ProductVariant } from "@KassaroAB/shared/types/productVariant";

const ProductsContainer = styled.div`
  margin-top: ${spacing.x3}px;
`;

const ProductsTitle = styled.div`
  margin-bottom: ${spacing.x2}px;
  font-weight: 600;
`;

const VariantNameCell = styled(TableCell)`
  padding-left: ${spacing.x4}px;
  color: ${colors.textMuted};
`;

interface ValidProductsTableProps {
  products: ImportValidProduct[];
}

const formatBarcode = (barcode: null | { code: string;}): string => {
  if (!barcode) return "-";
  return barcode.code || "-";
};

const formatExtraProductFieldValues = (values: Record<string, string> | null | undefined): string => {
  if (!values || Object.keys(values).length === 0) return "-";
  return Object.entries(values)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
};

const formatMoneyValue = (value: ImportValidProduct["unitPrice"] | null | undefined): string => {
  if (!value) return "-";
  return formatMoney(value);
};

const formatStockCount = (stockCount: number | null | undefined): string => {
  if (stockCount === null || stockCount === undefined) return "-";
  return String(stockCount);
};

const renderProductRow = (
  product: ImportValidProduct,
  rowKey: string,
) => (
  <TableRow key={rowKey}>
    <TableCell>{product.name || "-"}</TableCell>
    <TableCell>{product.group?.name || "-"}</TableCell>
    <TableCell>{product.articleNumber || "-"}</TableCell>
    <TableCell>{formatBarcode(product.barcode)}</TableCell>
    <TableCell align="right">{formatMoneyValue(product.unitPrice)}</TableCell>
    <TableCell>{formatMoneyValue(product.purchasePrice)}</TableCell>
    <TableCell align="right">{product.VAT}%</TableCell>
    <TableCell align="right">
      {product.takeawayTax !== null && product.takeawayTax !== undefined
        ? `${product.takeawayTax}%`
        : "-"}
    </TableCell>
    <TableCell align="right">{formatMoneyValue(product.takeawayPrice)}</TableCell>
    <TableCell>{product.takeawayBookkeepingAccount?.number || "-"}</TableCell>
    <TableCell align="right">{formatStockCount(product.stockCount)}</TableCell>
    <TableCell>{product.bookkeepingAccount?.number || "-"}</TableCell>
    <TableCell>{formatExtraProductFieldValues(product.extraProductFieldValues)}</TableCell>
  </TableRow>
);

const renderVariantRow = (
  variant: ProductVariant,
  rowKey: string,
  translate: (key: string, options?: Record<string, string | number>) => string,
) => (
  <TableRow key={rowKey}>
    <VariantNameCell>
      {translate("productImport.validVariantRow", {
        name: variant.name || variant.id,
      })}
    </VariantNameCell>
    <TableCell>-</TableCell>
    <TableCell>{variant.articleNumber || "-"}</TableCell>
    <TableCell>{variant.barcode ? formatBarcode(variant.barcode) : "-"}</TableCell>
    <TableCell align="right">{formatMoneyValue(variant.unitPrice)}</TableCell>
    <TableCell>{formatMoneyValue(variant.purchasePrice)}</TableCell>
    <TableCell>-</TableCell>
    <TableCell align="right">-</TableCell>
    <TableCell align="right">{formatMoneyValue(variant.takeawayPrice)}</TableCell>
    <TableCell>-</TableCell>
    <TableCell align="right">{formatStockCount(variant.stockCount)}</TableCell>
    <TableCell>-</TableCell>
    <TableCell>{formatExtraProductFieldValues(variant.extraFields ?? undefined)}</TableCell>
  </TableRow>
);

export const ValidProductsTable = ({ products }: ValidProductsTableProps) => {
  const { t } = useTranslation();

  if (products.length === 0) {
    return null;
  }

  return (
    <ProductsContainer>
      <ProductsTitle>{t("productImport.validProductsPreview")}</ProductsTitle>
      <Table>
        <TableHeader>
          <tr>
            <TableHeaderCell>{t("productImport.validName")}</TableHeaderCell>
            <TableHeaderCell>{t("productImport.validGroup")}</TableHeaderCell>
            <TableHeaderCell>{t("productImport.validArticleNumber")}</TableHeaderCell>
            <TableHeaderCell>{t("productImport.validBarcode")}</TableHeaderCell>
            <TableHeaderCell align="right">{t("productImport.validUnitPrice")}</TableHeaderCell>
            <TableHeaderCell>{t("productImport.validPurchasePrice")}</TableHeaderCell>
            <TableHeaderCell align="right">{t("productImport.validVat")}</TableHeaderCell>
            <TableHeaderCell align="right">{t("productImport.validTakeawayTax")}</TableHeaderCell>
            <TableHeaderCell align="right">{t("productImport.validTakeawayPrice")}</TableHeaderCell>
            <TableHeaderCell>{t("productImport.validTakeawayBookkeepingAccount")}</TableHeaderCell>
            <TableHeaderCell align="right">{t("productImport.validStock")}</TableHeaderCell>
            <TableHeaderCell>{t("productImport.validBookkeepingAccount")}</TableHeaderCell>
            <TableHeaderCell>{t("productImport.validExtraProductFields")}</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {products.flatMap((product, productIndex) => {
            const variants = product.variants ?? [];
            if (variants.length === 0) {
              return [renderProductRow(product, `product-${productIndex}`)];
            }

            return [
              renderProductRow(product, `product-${productIndex}`),
              ...variants.map((variant, variantIndex) =>
                renderVariantRow(variant, `product-${productIndex}-variant-${variantIndex}`, t),
              ),
            ];
          })}
        </TableBody>
      </Table>
    </ProductsContainer>
  );
};
